import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/* eslint-disable jsx-a11y/label-has-associated-control */
import { useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { useSelect } from 'downshift';
import { BaseInputHelperText, BaseInputLabelText, DropdownListItemsDefault } from "../shared";
// SubComponents
import DropdownButton from "./DropdownButton";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var itemToString = function itemToString(option) {
  return (option === null || option === void 0 ? void 0 : option.label) || '';
};
export var Dropdown = function Dropdown(_ref) {
  var items = _ref.items,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    label = _ref.label,
    _ref$full = _ref.full,
    full = _ref$full === void 0 ? false : _ref$full,
    _ref$sizeType = _ref.sizeType,
    sizeType = _ref$sizeType === void 0 ? 'large' : _ref$sizeType,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'l' : _ref$size,
    isOpenProp = _ref.isOpen,
    defaultIsOpen = _ref.defaultIsOpen,
    helperText = _ref.helperText,
    _ref$error = _ref.error,
    error = _ref$error === void 0 ? false : _ref$error,
    defaultSelectedItem = _ref.defaultSelectedItem,
    tooltip = _ref.tooltip,
    placeholder = _ref.placeholder,
    id = _ref.id,
    useSelectHookProps = _ref.useSelectHookProps,
    onSelectProp = _ref.onSelect,
    selectedItemProp = _ref.selectedItem,
    _ref$clampSelectedIte = _ref.clampSelectedItem,
    clampSelectedItem = _ref$clampSelectedIte === void 0 ? false : _ref$clampSelectedIte,
    _ref$multilineOption = _ref.multilineOption,
    multilineOption = _ref$multilineOption === void 0 ? false : _ref$multilineOption,
    _ref$displayPosition = _ref.displayPosition,
    displayPosition = _ref$displayPosition === void 0 ? 'bottom' : _ref$displayPosition,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className;
  var buttonRef = useRef(null);
  var _useState = useState(displayPosition),
    _useState2 = _slicedToArray(_useState, 2),
    displayPositionState = _useState2[0],
    setDisplayPositionState = _useState2[1];
  var _useSelect = useSelect(_objectSpread({
      items: items,
      itemToString: itemToString,
      id: id ? "compass.ui.dropdown-".concat(id) : undefined,
      defaultSelectedItem: defaultSelectedItem,
      onSelectedItemChange: function onSelectedItemChange(data) {
        onSelectProp(_objectSpread(_objectSpread({}, data), {}, {
          selectedItem: data.selectedItem || null
        }));
      },
      isOpen: isOpenProp,
      selectedItem: selectedItemProp
    }, useSelectHookProps)),
    isOpen = _useSelect.isOpen,
    selectedItem = _useSelect.selectedItem,
    highlightedIndex = _useSelect.highlightedIndex,
    getToggleButtonProps = _useSelect.getToggleButtonProps,
    getLabelProps = _useSelect.getLabelProps,
    getMenuProps = _useSelect.getMenuProps,
    getItemProps = _useSelect.getItemProps,
    openMenu = _useSelect.openMenu;

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(function () {
    if (defaultIsOpen) {
      openMenu();
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(function () {
    if (displayPosition === 'responsive') {
      var handleResponsivePositioning = function handleResponsivePositioning() {
        if (buttonRef.current) {
          var buttonRect = buttonRef.current.getBoundingClientRect();
          var viewportHeight = window.innerHeight;
          var spaceBelow = viewportHeight - buttonRect.top;
          var spaceAbove = buttonRect.top;
          if (spaceBelow < spaceAbove) {
            setDisplayPositionState('top');
          } else {
            setDisplayPositionState('bottom');
          }
        }
      };
      handleResponsivePositioning();
      window.addEventListener('resize', handleResponsivePositioning);
      return function () {
        window.removeEventListener('resize', handleResponsivePositioning);
      };
    }
  }, [displayPosition, isOpen]);
  return /*#__PURE__*/_jsxs("div", {
    className: clsx('tw-relative', full ? 'tw-w-full' : 'tw-w-[17.2rem]', className),
    children: [label &&
    /*#__PURE__*/
    // biome-ignore lint/a11y/noLabelWithoutControl: label association handled differently
    _jsx("label", _objectSpread(_objectSpread({
      className: clsx('tw-flex tw-flex-col tw-mb-0.8', {
        'tw-cursor-not-allowed': disabled
      })
    }, getLabelProps({
      disabled: disabled
    })), {}, {
      children: /*#__PURE__*/_jsx(BaseInputLabelText, {
        label: label,
        tooltip: tooltip,
        disabled: disabled
      })
    })), /*#__PURE__*/_jsx(DropdownButton, {
      ref: buttonRef,
      disabled: disabled,
      isOpen: isOpen,
      size: size || (sizeType === 'large' ? 'l' : 's'),
      error: error,
      buttonProps: getToggleButtonProps({
        disabled: disabled
      }),
      selectedItem: selectedItem,
      placeholder: placeholder,
      clampSelectedItem: clampSelectedItem
    }), /*#__PURE__*/_jsx("div", {
      className: clsx('tw-list-none tw-absolute tw-left-0 tw-m-0 tw-z-7 tw-rounded-1', {
        'tw-w-28': !full,
        'tw-w-full': full,
        'tw-hidden': !isOpen,
        'tw-bottom-full': displayPositionState === 'top',
        'tw-top-full': displayPositionState === 'bottom'
      }),
      style: {
        top: displayPositionState === 'top' ? 'auto' : 'calc(100% +   1.2rem)',
        bottom: displayPositionState === 'top' ? 'calc(100% +   1.22rem)' : 'auto'
      },
      children: /*#__PURE__*/_jsx("div", {
        className: "tw-z-1 tw-rounded-1",
        children: /*#__PURE__*/_jsx(DropdownListItemsDefault, {
          items: items,
          getUlProps: getMenuProps,
          getLiProps: getItemProps,
          isOpen: isOpen,
          disabled: disabled,
          selectedItem: selectedItem,
          highlightedIndex: highlightedIndex,
          multilineOption: multilineOption,
          size: size || sizeType === 'large' ? 'l' : 's'
        })
      })
    }), helperText && /*#__PURE__*/_jsx("div", {
      className: "tw-mt-0.8",
      children: /*#__PURE__*/_jsx(BaseInputHelperText, {
        text: helperText,
        error: error
      })
    })]
  });
};
export default Dropdown;